import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, LEAD_KINDS } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", message: "" };

export const LeadForm = ({ programmes = [], defaultProgrammeId = "none", defaultKind = "contact", dark = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [kind, setKind] = useState(defaultKind);
  const [programmeId, setProgrammeId] = useState(defaultProgrammeId);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/40" : "bg-white";
  const label = dark ? "text-white/70" : "text-[#756D69]";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, kind, programme_id: programmeId === "none" ? null : programmeId });
      setSent(true);
      setForm(EMPTY);
      toast.success("Votre demande a bien été envoyée. Nous vous recontactons rapidement.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent)
    return (
      <div className={`p-8 rounded-sm border ${dark ? "border-white/15 text-white" : "border-[#2F2A28]/10 bg-white"}`} data-testid="lead-form-success">
        <CheckCircle2 className="h-10 w-10 text-[#C88A58]" />
        <p className="font-display text-3xl mt-4">Merci !</p>
        <p className={`mt-2 text-sm ${label}`}>Votre demande a bien été reçue. Notre équipe vous recontacte très vite.</p>
        <button onClick={() => setSent(false)} className="mt-6 text-sm underline text-[#C88A58]" data-testid="lead-form-new-button">
          Envoyer une autre demande
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="flex flex-wrap gap-2">
        {Object.entries(LEAD_KINDS).map(([k, l]) => (
          <button
            type="button"
            key={k}
            onClick={() => setKind(k)}
            data-testid={`lead-kind-${k}`}
            className={`px-4 py-2 rounded-full text-xs tracking-wide border transition-colors duration-300 ${
              kind === k ? "bg-[#C88A58] border-[#C88A58] text-white" : dark ? "border-white/20 text-white/70 hover:border-white/50" : "border-[#2F2A28]/15 text-[#2F2A28] hover:border-[#2F2A28]/40"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2 sm:col-span-2">
          <Label className={label}>Nom complet</Label>
          <Input required value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
        </div>
        <div className="space-y-2">
          <Label className={label}>Email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
        </div>
        <div className="space-y-2">
          <Label className={label}>Téléphone / WhatsApp</Label>
          <Input required value={form.phone} onChange={set("phone")} placeholder="+221 ..." className={field} data-testid="contact-phone-input" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className={label}>Programme d'intérêt</Label>
          <Select value={programmeId} onValueChange={setProgrammeId}>
            <SelectTrigger className={field} data-testid="contact-programme-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun en particulier</SelectItem>
              {programmes.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className={label}>Message</Label>
          <Textarea rows={4} value={form.message} onChange={set("message")} placeholder="Vos disponibilités, votre budget, vos questions..." className={field} data-testid="contact-message-input" />
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="contact-submit-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#C88A58] hover:bg-[#b57849] disabled:opacity-60 text-white rounded-full text-sm tracking-wide transition-colors duration-300">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer ma demande
      </button>
    </form>
  );
};
